import { Column, Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import { PurchaseOrderApprovalEventType } from '../../enums/purchase-order-approval-status.enum';
import { BaseEntity } from './base.entity';
import { PurchaseOrderEntity } from './purchase-order.entity';

/**
 * Audit-history row for every transition through the PO approval
 * gate. Sales orders (the model we mirrored otherwise) do NOT have
 * a separate audit table — they overwrite `overrideDateTime` and
 * `fkUserIdOverride` on the order row itself, so a re-submit / re-
 * reject cycle loses the prior decision. We pay one extra row per
 * event here so a manager can answer "why was this rejected last
 * time?" without log-diving.
 *
 * One row per event (submitted / approved / rejected / resubmitted).
 * The acting user is captured via `BaseEntity._creator_userId` —
 * we don't need a separate `fk_actor__id` column because audit
 * rows are insert-only (no other write triggers `_creator_userId`
 * to overwrite the original actor).
 *
 * `reason` is populated when the event is `rejected` (manager-
 * supplied, required) or `submitted` (system-generated JSON
 * snapshot of the rules that tripped). Approve / resubmit events
 * leave it null unless the actor adds a comment in a future iter.
 */
@Index('purchase_order_approval_event__fk_po__id_fk', ['fkPurchaseOrderId'], {})
@Index('purchase_order_approval_event__event_type_idx', ['eventType'], {})
@Entity('purchase_order_approval_event')
export class PurchaseOrderApprovalEventEntity extends BaseEntity {
	@Column('int', {
		name: '_fk_purchase_order__id',
		unsigned: true,
	})
	public fkPurchaseOrderId!: number;

	@Column('varchar', { name: 'eventType', length: 16 })
	public eventType!: PurchaseOrderApprovalEventType;

	@Column('text', { name: 'reason', nullable: true })
	public reason!: string | null;

	@ManyToOne(
		() => PurchaseOrderEntity,
		(purchaseOrder) => purchaseOrder.approvalEvents,
		{
			onDelete: 'CASCADE',
			onUpdate: 'RESTRICT',
		},
	)
	@JoinColumn([{ name: '_fk_purchase_order__id', referencedColumnName: 'id' }])
	public fkPurchaseOrder!: PurchaseOrderEntity;
}
